/**
 * Who is receiving the money.
 *
 * First on the screen and never collapsed, because the one irreversible mistake available on this
 * page is giving to the wrong organization, and it is silent when it happens. The mark, the name, and
 * the town do the identification together — two organizations in the same city share a name far more
 * often than they share both.
 */
import type { ContextResponse, GivingSeason } from "@givehere/contracts";
import { MenuIcon, PersonIcon } from "./icons.js";
import { OrgMark } from "./OrgMark.js";

type Organization = ContextResponse["organization"];

export function OrgHeader({
  organization,
  eventName,
  season = null,
  onOpenMenu,
  onOpenProfile,
  remembered = false,
  profileLabel = "Check in at this event",
}: {
  organization: Organization;
  /**
   * The event this page is for, or null where there is none.
   *
   * Null is the organization that has not claimed its listing (DEC-042): it has no events at all,
   * and a line saying so — or an empty one holding space — would be the header describing an
   * absence rather than the organization.
   */
  eventName: string | null;
  /**
   * The organization's running giving season, when the server says one is running.
   *
   * Its presence is the whole switch into the design's peak layout: the mark shrinks, the
   * identity moves beside it instead of above it, and the amount comes up into the first screen.
   * The client never decides this — it holds a label or a null, never a date range.
   */
  season?: GivingSeason | null;
  /** When set, the donor page's two doors ride in this header: hamburger and profile. */
  onOpenMenu?: (() => void) | undefined;
  onOpenProfile?: (() => void) | undefined;
  /** Puts the quiet "you are remembered" dot on the profile icon. */
  remembered?: boolean;
  /**
   * The stranger's name for the profile door (GH-856): "Check in at this event" on a live event,
   * "Sign in" anywhere else — on an organization's or a fund's page the door is sign-in, so the
   * label a screen reader announces must not promise a check-in that is not there.
   */
  profileLabel?: string;
}) {
  return (
    /* `data-season` rather than a class, because it is a state and not a variant: the same header
       with the same parts, laid out tighter for as long as the organization says its season is
       running. Every rule that reads it is written against `[data-season]` in one place. */
    <header className="org" data-season={season ? "" : undefined}>
      {(onOpenMenu || onOpenProfile) && (
        /*
          A row, not two controls floating over the identity.

          These were `position: absolute` into the corners of a centred block, and on the tickets
          page — where this header leads with the name rather than the mark — the profile control
          landed on top of the organization's own name. Peak mode had already met the problem and
          patched it in that one state, with `padding-inline: calc(var(--tap) - .25rem)` and a
          comment saying the controls are positioned over the row; the ordinary state had no such
          allowance and so collided.

          Exactly the defect fixed on the directory header the same day, in a second component.
          A row removes it for every state at once, including the two that had been reasoned about
          separately, and lets peak mode drop its padding hack.
        */
        <div className="org__bar">
          {onOpenMenu && (
            <button
              type="button"
              className="org__corner org__corner--menu"
              aria-label="Menu"
              onClick={onOpenMenu}
            >
              <MenuIcon />
            </button>
          )}
          {onOpenProfile && (
            <button
              type="button"
              className={
                remembered
                  ? "org__corner org__corner--profile org__corner--in"
                  : "org__corner org__corner--profile"
              }
              aria-label={remembered ? "Your profile" : profileLabel}
              onClick={onOpenProfile}
            >
              <PersonIcon />
              {remembered && <span className="home__profile-dot" aria-hidden="true" />}
            </button>
          )}
        </div>
      )}
      <OrgMark
        displayName={organization.displayName}
        logoUrl={organization.logoUrl}
        className="org__mark"
        size={64}
      />

      {/* `dir="auto"` on every element whose whole content is written by the tenant. A name in
          Arabic or Hebrew is a right-to-left paragraph, and a browser has no way to know that
          from an English page: without this the letters shape correctly and the *phrase* runs
          backwards, with its punctuation stranded on the wrong end. The attribute reads the first
          strong character and decides, which is the one heuristic that needs no locale, no server
          field and no guess about who the tenant is. */}
      {/* A wrapper with no role, so the accessibility tree — and the semantic snapshots built
          from it — are identical either way. It exists to give the compressed layout a single
          column to place beside the mark. */}
      <div className="org__ident">
        {season && (
          /* The organization's own words for its own calendar, and tenant-authored like every
             other line here — so it reads its own direction. */
          <p className="season-label" dir="auto">
            {season.label}
          </p>
        )}
        <h1 className="org__name" dir="auto">
          {organization.displayName}
        </h1>
        {organization.locality && (
          <p className="org__locality" dir="auto">
            {organization.locality}
          </p>
        )}
        {eventName && (
          <p className="org__event" dir="auto">
            {eventName}
          </p>
        )}
      </div>
    </header>
  );
}
